import React, { FC, useState } from 'react'
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Styles
import './about-profile.scss';

// Shared UI Library Components
import {
  CustomButton,
  CustomTextArea, DiscardModal,
  InputReadOnly
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Store entities
import { updateCounsellorAbout } from '../../../../store/current-counsellor/current-counsellor.thunks';
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';

type TAboutProfileProps = {
  about: string;
  worker_id: string | number;
};

export const AboutProfile: FC<TAboutProfileProps> = (
  { about, worker_id }: TAboutProfileProps
) => {
  const [form] = Form.useForm<{ about: string }>();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const onValuesChange = (_: { [key: string]: string | string[] }, allValues: { about: string }): void => {
    setHasChanges(about !== allValues.about);
  }

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    toggleEditMode();
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      form.resetFields();
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();
      return;
    }
    toggleDiscardModal();
  }

  const saveChanges = async (): Promise<void> => {
    try {
      const { about } = await form.validateFields();
      setSavingChanges(true);
      const { payload } = await dispatch(updateCounsellorAbout({ about, worker_id }));
      setSavingChanges(false);
      if (!payload) return;
      setEditMode(false);
      setHasChanges(false);
      form.resetFields();
    } catch (e) {
      const fieldError = form.getFieldError('about');
      setHasChanges(false);
      dispatch(openNotification({
        type: 'error',
        description: fieldError[0]
      }));
    }
  }

  return (
    <div className="about-profile">
      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />
      <div className="about-profile--label personal-data--label">
        <div className="label-title">
          About
        </div>
        <div className="label-actions">
          { !editMode &&
            <CustomButton type={ 'text' }
                          text={ 'Edit' }
                          size={ 'small' }
                          onClick={ toggleEditMode }/>
          }

          { editMode && <>
            { hasChanges && <CustomButton type={ 'primary' }
                                          text={ 'Save' }
                                          size={ 'small' }
                                          loading={ savingChanges }
                                          onClick={ saveChanges }/> }
            <CustomButton type={ 'text' }
                          text={ 'Cancel' }
                          size={ 'small' }
                          disabled={ savingChanges }
                          onClick={ onCancelChangesHandler }/>
          </> }
        </div>
      </div>
      <Form className="personal-link--content personal-data--content" form={ form } onValuesChange={ onValuesChange }>
        <div className="content-item">
          {
            !editMode && <InputReadOnly
              label={ 'About' }
              value={ about }
            />
          }

          {
            editMode &&
            <Form.Item name={ 'about' } initialValue={ about }>
              <CustomTextArea
                placeholder={ 'About' }
                maxLength={ 500 }
                autoSize={ {
                  minRows: 2,
                  maxRows: 20
                } }
                showCount={ true }
              />
            </Form.Item>
          }
        </div>
      </Form>
    </div>
  );
}
